import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Divider,
  Box,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import IconCheckbox from "../../components/input/new/iconCheckbox";
import {
  practiceAreasHierarchy,
  topLanguagesUSA,
} from "../../helpers/stringHelper";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import RadioButton from "../../components/input/new/radioButton";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import "./filterMenu.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const FilterMenu = ({ filters, onFilterChange }) => {
  const commonMenuListStyle = {
    minWidth: "420px",
    maxHeight: "650px",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    paddingLeft: 3,
    paddingRight: 3,
    boxShadow: "8px 8px 53.5px 0px #00000014",
  };

  const pickerTheme = createTheme({
    palette: {
      primary: {
        main: "#144A8C",
      },
    },
  });

  const filterMenuStyles = {
    fontFamily: "Poppins",
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "130%",
    letterSpacing: "-0.17px",
    textAlign: "left",
    verticalAlign: "middle",
    color: "#333333",
    pl: 2, // ~16px
    pr: 2, // you can even omit this if you want no right padding
    py: 1.5, // ~12px vertical padding
    mx: 0,
    my: 0,
    borderRadius: "8px",
  };
  const [menuState, setMenuState] = useState({
    openMenu: null,
    anchorEl: null,
  });

  const distanceOptions = [
    { label: "Within 5 miles", value: 5 },
    { label: "Within 10 miles", value: 10 },
    { label: "Within 50 miles", value: 50 },
    { label: "Within 100 miles", value: 100 },
  ];
  const [selectedWhen, setSelectedWhen] = useState("flexible");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("timeframe");

  // Return a function that opens the menu with a given menu name.
  const handleMenuOpen = (menuName) => (event) => {
    setMenuState({ openMenu: menuName, anchorEl: event.currentTarget });
  };

  const handleMenuClose = () => {
    setMenuState({ openMenu: null, anchorEl: null });
  };

  return (
    <div className="filters-row">
      {/* Flexible filter */}
      <button
        aria-controls="menu-flexible"
        aria-haspopup="true"
        onClick={handleMenuOpen("menu-flexible")}
        className="filter-btn"
      >
        I’m flexible
      </button>

      {/* updated Menu */}
      <Menu
        id="menu-flexible"
        anchorEl={menuState.anchorEl}
        open={menuState.openMenu === "menu-flexible"}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 50, horizontal: 20 }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ sx: commonMenuListStyle }}
      >
        <p
          style={{
            fontFamily: "Poppins",
            fontSize: "14px",
            color: "#000000D9",
            marginTop: "1rem",
          }}
        >
          What is your preferred timeframe?
        </p>
        <div className="tabs-container">
          <button
            className={`tab ${activeTab === "timeframe" ? "active" : ""}`}
            onClick={() => setActiveTab("timeframe")}
          >
            Timeframe
            {activeTab === "timeframe" && <span className="check">✓</span>}
          </button>
          <button
            className={`tab ${activeTab === "date" ? "active" : ""}`}
            onClick={() => setActiveTab("date")}
          >
            Date
            {activeTab === "date" && <span className="check">✓</span>}
          </button>
        </div>

        {/** 2) Timeframe panel **/}
        {activeTab === "timeframe" && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", mb: 2, gap: "10px" }}
            >
              <RadioButton
                name="when"
                value="flexible"
                checked={selectedWhen === "flexible"}
                onChange={() => setSelectedWhen("flexible")}
                label="I’m flexible"
              />
              <p className="filter-help-text">
                We will show the best match for you
              </p>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <RadioButton
                name="when"
                value="immediate"
                checked={selectedWhen === "immediate"}
                onChange={() => setSelectedWhen("immediate")}
                label="Immediate"
              />
              <p className="filter-help-text">
                We will show you the lawyers who can talk to you soonest
              </p>
            </Box>
          </Box>
        )}

        {/* 3) Date panel */}
        {activeTab === "date" && (
          <Box sx={{ p: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ThemeProvider theme={pickerTheme}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={selectedDate}
                  onChange={(d) => setSelectedDate(d)}
                />
              </ThemeProvider>
            </LocalizationProvider>
          </Box>
        )}

        {/** 4) Divider + fixed footer **/}
        <Divider />
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            backgroundColor: "background.paper",
          }}
        >
          <Button
            onClick={() => {
              // clear both timeframe/date
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onFilterChange({
                ...filters,
                when: activeTab === "timeframe" ? selectedWhen : undefined,
                date: activeTab === "date" ? selectedDate : undefined,
              });
              // apply selectedWhen or selectedDate
              handleMenuClose();
            }}
            sx={{
              backgroundColor: "#144A8C",
              "&:hover": { backgroundColor: "#0F3F7A" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>

      {/* Time of Day filter */}
      <button
        aria-controls="menu-time-of-day"
        aria-haspopup="true"
        onClick={handleMenuOpen("menu-time-of-day")}
        className="filter-btn"
      >
        Time of day
      </button>
      <Menu
        id="menu-time-of-day"
        anchorEl={menuState.anchorEl}
        open={menuState.openMenu === "menu-time-of-day"}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 50, horizontal: 20 }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ sx: commonMenuListStyle }}
      >
        {[
          { label: "Morning", value: "morning", desc: "Starts before 10AM" },
          {
            label: "Afternoon",
            value: "afternoon",
            desc: "Between 10AM to 4PM",
          },
          { label: "Evening", value: "evening", desc: "After 4PM" },
        ].map(({ label, value, desc }) => (
          <MenuItem
            key={value}
            disableRipple
            sx={filterMenuStyles}
            onClick={() => {
              onFilterChange({ ...filters, timeOfDay: value });
              handleMenuClose();
            }}
          >
            <IconCheckbox
              label={label}
              checked={filters.timeOfDay === value}
              onChange={(checked) => {
                onFilterChange({
                  ...filters,
                  timeOfDay: checked ? value : "",
                });
                handleMenuClose();
              }}
            />
            <Box sx={{ ml: 3 }}>
              <Typography
                variant="body2"
                sx={{ fontSize: "14px", color: "#999999" }}
              >
                {desc}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* Clear & Apply */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            px: 1,
            pb: 1,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              onFilterChange({ ...filters, timeOfDay: "" });
              handleMenuClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleMenuClose}
            sx={{
              backgroundColor: "#144A8C",
              "&:hover": {
                backgroundColor: "#0F3F7A",
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>

      {/* Practice Area filter */}
      <button
        aria-controls="menu-practice-area"
        aria-haspopup="true"
        onClick={handleMenuOpen("menu-practice-area")}
        className="filter-btn"
      >
        Practice Area
      </button>
      <Menu
        id="menu-practice-area"
        anchorEl={menuState.anchorEl}
        open={menuState.openMenu === "menu-practice-area"}
        onClose={handleMenuClose}
        MenuListProps={{ sx: commonMenuListStyle }}
      >
        {/* scrollable container */}
        <Box sx={{ overflowY: "auto", pr: 1 }}>
          {practiceAreasHierarchy.map((cat) => (
            <React.Fragment key={cat.value}>
              {/* parent */}
              <MenuItem
                disableRipple
                sx={{ ...filterMenuStyles, pl: 2 }}
                onClick={() => {
                  const next =
                    filters.practiceArea === cat.value ? "" : cat.value;
                  onFilterChange({ ...filters, practiceArea: next });
                }}
              >
                <IconCheckbox
                  label={cat.label}
                  checked={filters.practiceArea === cat.value}
                  onChange={(checked) => {
                    const next = checked ? cat.value : "";
                    onFilterChange({ ...filters, practiceArea: next });
                  }}
                />
              </MenuItem>

              {/* children */}
              {cat.children.map((child) => (
                <MenuItem
                  key={child.value}
                  disableRipple
                  sx={{ ...filterMenuStyles, pl: 4 }}
                  onClick={() => {
                    const next =
                      filters.practiceArea === child.value ? "" : child.value;
                    onFilterChange({ ...filters, practiceArea: next });
                  }}
                >
                  <IconCheckbox
                    label={child.label}
                    checked={filters.practiceArea === child.value}
                    onChange={(checked) => {
                      const next = checked ? child.value : "";
                      onFilterChange({ ...filters, practiceArea: next });
                    }}
                  />
                </MenuItem>
              ))}
            </React.Fragment>
          ))}
        </Box>

        <Divider />

        {/* Clear & Apply */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 1,
            px: 1,
            pb: 1,
            gap: 1,
            backgroundColor: "background.paper",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              onFilterChange({ ...filters, practiceArea: "" });
              handleMenuClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleMenuClose();
            }}
            sx={{
              backgroundColor: "#144A8C",
              "&:hover": { backgroundColor: "#0F3F7A" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>

      <button
        aria-controls="menu-distance"
        aria-haspopup="true"
        onClick={handleMenuOpen("menu-distance")}
        className="filter-btn"
      >
        Distance
      </button>
      <Menu
        id="menu-distance"
        anchorEl={menuState.anchorEl}
        open={menuState.openMenu === "menu-distance"}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 50, horizontal: 20 }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ sx: commonMenuListStyle }}
      >
        {distanceOptions.map(({ label, value }) => {
          const isChecked = filters.radius === value;
          return (
            <MenuItem key={value} disableRipple sx={filterMenuStyles}>
              <IconCheckbox
                label={label}
                checked={isChecked}
                onChange={(newChecked) => {
                  onFilterChange({
                    ...filters,
                    radius: newChecked ? value : null,
                  });
                  handleMenuClose();
                }}
              />
            </MenuItem>
          );
        })}

        <Divider />

        {/* Clear & Apply */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            px: 1,
            pb: 1,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              handleMenuClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleMenuClose}
            sx={{
              backgroundColor: "#144A8C",
              "&:hover": {
                backgroundColor: "#0F3F7A", // slightly darker for hover
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>

      {/* Language Spoken filter */}
      {/* Language Spoken filter */}
      <button
        aria-controls="menu-language-spoken"
        aria-haspopup="true"
        onClick={handleMenuOpen("menu-language-spoken")}
        className="filter-btn"
      >
        Languages
      </button>
      <Menu
        id="menu-language-spoken"
        anchorEl={menuState.anchorEl}
        open={menuState.openMenu === "menu-language-spoken"}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 50, horizontal: 20 }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ sx: commonMenuListStyle }}
      >
        {/* Scrollable list */}
        <Box
          sx={{
            overflowY: "auto",
            pr: 1, // room for scrollbar
          }}
        >
          {topLanguagesUSA.map((lang) => {
            const isChecked = filters.languageSpoken === lang;
            return (
              <MenuItem
                key={lang}
                disableRipple
                sx={filterMenuStyles}
                onClick={() => {
                  onFilterChange({
                    ...filters,
                    languageSpoken: isChecked ? "" : lang,
                  });
                }}
              >
                <IconCheckbox
                  label={lang}
                  checked={isChecked}
                  onChange={(checked) => {
                    onFilterChange({
                      ...filters,
                      languageSpoken: checked ? lang : "",
                    });
                  }}
                />
              </MenuItem>
            );
          })}
        </Box>

        <Divider />

        {/* Fixed Clear & Apply */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 1,
            px: 1,
            pb: 1,
            gap: 1,
            backgroundColor: "background.paper",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              onFilterChange({
                ...filters,
                languageSpoken: "",
              });
              handleMenuClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleMenuClose}
            sx={{
              backgroundColor: "#144A8C",
              "&:hover": { backgroundColor: "#0F3F7A" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>

      {/* Experience filter */}
      <button
        aria-controls="menu-experience"
        aria-haspopup="true"
        onClick={handleMenuOpen("menu-experience")}
        className="filter-btn"
      >
        Experience
      </button>
      <Menu
        id="menu-experience"
        anchorEl={menuState.anchorEl}
        open={menuState.openMenu === "menu-experience"}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 50, horizontal: 20 }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ sx: commonMenuListStyle }}
      >
        {[
          { value: "level-1", label: "1-5 years" },
          { value: "level-2", label: "5-10 years" },
          { value: "level-3", label: "10+ years" },
        ].map(({ value, label }) => (
          <MenuItem
            key={value}
            disableRipple
            sx={filterMenuStyles}
            onClick={() => {
              onFilterChange({ ...filters, experience: value });
              handleMenuClose();
            }}
          >
            <IconCheckbox
              label={label}
              checked={filters.experience === value}
              onChange={(checked) => {
                onFilterChange({
                  ...filters,
                  experience: checked ? value : "",
                });
                handleMenuClose();
              }}
            />
          </MenuItem>
        ))}

        <Divider />

        {/* Clear & Apply */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            px: 1,
            pb: 1,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              onFilterChange({ ...filters, experience: "" });
              handleMenuClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleMenuClose}
            sx={{
              backgroundColor: "#144A8C",
              "&:hover": { backgroundColor: "#0F3F7A" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>

      {/* Firm Size or Solo Practitioner filter */}
      <button
        aria-controls="menu-firm-size"
        aria-haspopup="true"
        onClick={handleMenuOpen("menu-firm-size")}
        className="filter-btn"
      >
        Firm Size
      </button>
      <Menu
        id="menu-firm-size"
        anchorEl={menuState.anchorEl}
        open={menuState.openMenu === "menu-firm-size"}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 50, horizontal: 20 }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ sx: commonMenuListStyle }}
      >
        {[
          { label: "Solo Practitioner", value: "solo" },
          { label: "Small Firm 2 - 10 Lawyers", value: "small" },
          { label: "Medium Firm 11 - 50 Lawyers", value: "medium" },
          { label: "Large Firm 50+ Lawyers", value: "large" },
        ].map(({ value, label }) => (
          <MenuItem
            key={value}
            disableRipple
            sx={filterMenuStyles}
            onClick={() => {
              onFilterChange({ ...filters, firmSize: value });
              handleMenuClose();
            }}
          >
            <IconCheckbox
              label={label}
              checked={filters.firmSize === value}
              onChange={(checked) => {
                onFilterChange({
                  ...filters,
                  firmSize: checked ? value : "",
                });
                handleMenuClose();
              }}
            />
          </MenuItem>
        ))}

        <Divider />

        {/* Clear & Apply */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            px: 1,
            pb: 1,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              onFilterChange({ ...filters, firmSize: "" });
              handleMenuClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleMenuClose}
            sx={{
              backgroundColor: "#144A8C",
              "&:hover": { backgroundColor: "#0F3F7A" },
            }}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </div>
  );
};

export default FilterMenu;
